import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import classnames from 'classnames';
import { t } from 'services/translation';
import './index.scss';
import { WarningIcon as WarningIcn } from 'views/ui/icons';
import { isActionKey } from 'ui_system/utilities/KeyboardHelpers';
import {
  CapacityWarning,
  formatOverbookingMessage,
} from 'views/anyguide/bookings/alter/form/CapacityWarningHelpers';
import { Theme } from 'ui_system/theme';
import TitlebarActions from 'actions/titlebar';
import ModalActions from 'actions/modal';
import AlterBookingModal from 'views/anyguide/bookings/alter';
import { ReactModal } from 'ui_system/modals/ReactModal';
import MultiSelectOption from './option/MultiSelectOption';
import { MultiSelectGuestOption } from './GuestOfGuest/MultiSelectGuestOption';
import { MultiSelectGuestDesktop } from './GuestOfGuest/MultiSelectGuest';
import { buildCollapsedLabel, buildEditBookingLabel } from './guests/Helpers';

const WarningIcon = styled(WarningIcn)`
  fill: #d82f2f;
`;

const { number, array, object, func } = PropTypes;

const GuestListModal = styled(
  ReactModal(MultiSelectGuestOption, {
    isBackgroundClickable: false,
    isCloseButtonHidden: true,
  })
)`
  padding: 0;
  @media (min-width: ${Theme.mobile.phoneWidth}px) {
    width: 700px;
  }
`;

class MultiSelectDropdown extends React.Component {
  static propTypes = {
    min: number.isRequired,
    max: number.isRequired,
    rootLabels: object.isRequired,
    options: array.isRequired,
    onChange: func,
    batchUnitsUpdate: PropTypes.func,
    removeGuest: PropTypes.func,
    guestList: PropTypes.array,
    inPlaceEdit: PropTypes.bool,
    for_guest: PropTypes.bool,
    disabled: PropTypes.bool,
    bookingId: number.isRequired,
    country: PropTypes.string,
    resetGuestList: PropTypes.func,
    initialTicketQuantity: number.isRequired,
    capacityError: PropTypes.string,
    slotTicketCapacities: PropTypes.array,
    hasTicketCapacityEnabled: PropTypes.bool,
    timeSelected: PropTypes.bool,
    tourTicketCapacities: PropTypes.array,
    capacityErrorJobInProgress: PropTypes.string,
    isEditingCurrentSlot: PropTypes.bool,
    overbookedTicketCapacities: PropTypes.array,
    hasBookingUpdated: PropTypes.bool,
    isAAB: PropTypes.bool,
  };

  static defaultProps = {
    inPlaceEdit: false,
    for_guest: false,
    disabled: false,
    isAAB: false,
  };

  constructor(props) {
    super(props);
    this.state = {
      expanded: false,
      showGuestListModal: false,
      expandedId: '',
      doneClicked: false,
    };
  }

  componentDidUpdate(prevProps) {
    const {
      capacityError,
      capacityErrorJobInProgress,
      slotTicketCapacities = [],
      hasTicketCapacityEnabled,
      timeSelected,
      value,
    } = this.props;

    const {
      capacityError: prevCapacityError,
      capacityErrorJobInProgress: prevCapacityErrorJobInProgress,
    } = prevProps;

    const firstItem = value?.length > 0 ? value[0] : null;

    const slotTicketCapacity = hasTicketCapacityEnabled
      ? slotTicketCapacities.find(
          (capacity) => capacity.unit_type_id === firstItem?.id
        )
      : null;

    // Only expand if there's a new, unique capacity error (different timestamp)
    // or if the first ticket is unavailable in collapsed state
    const isCapacityError = this.hasChangedToTrue(
      capacityError,
      prevCapacityError
    );

    const isCapacityErrorJobInProgress = this.hasChangedToTrue(
      capacityErrorJobInProgress,
      prevCapacityErrorJobInProgress
    );

    const isFirstTicketUnavailable =
      slotTicketCapacity && slotTicketCapacity.availability < 1;

    const shouldExpandUnlessDone =
      (isFirstTicketUnavailable || timeSelected) && !this.state.doneClicked;

    if (
      (isCapacityError ||
        isCapacityErrorJobInProgress ||
        shouldExpandUnlessDone) &&
      !this.state.expanded
    ) {
      this.setState({ expanded: true });
    }
  }

  hasChangedToTrue = (value, prevValue) => {
    return value !== prevValue && Boolean(value);
  };

  onDecrease = (id) => {
    if (this.isDecreasable()) {
      this.props.onChange(id, -1);
    }
  };

  onIncrease = (id) => {
    this.props.onChange(id, +1);
  };

  onChange = (id, newValue, totalChange) => {
    const { totalCount, min, max } = this.props;
    // prevent total value from being out of range of the min/max
    const newTotal = totalCount - totalChange;
    if (newTotal >= min && newTotal <= max) {
      this.props.onChange(id, null, newValue);
    } else if (newTotal < min) {
      this.props.onChange(id, null, 1);
    }
  };

  isDecreasable() {
    const { totalCount } = this.props;
    return totalCount > 0;
  }

  expand = () => {
    const { isGuestOfGuestEnabled, disabled } = this.props;
    if (disabled) return;
    this.setState({ expanded: true, doneClicked: false });
    const { isMobile } = Theme.mobile;
    const shouldShowGogEditBooking = isGuestOfGuestEnabled;
    if (isMobile && shouldShowGogEditBooking) {
      this.setState({
        editBookingStep: 2,
        showGuestListModal: true,
      });
    }
  };

  multiSelectDone = () => {
    const { totalCount, min, max } = this.props;
    const disabled = totalCount < min || totalCount > max;

    if (!disabled) {
      this.setState({ expanded: false, doneClicked: true });
    }
  };

  onCloseGuestModal = () => {
    this.props.resetGuestList();
    this.setState({ showGuestListModal: false, expanded: false });
  };

  _renderDropdown() {
    const {
      for_guest,
      isGuestOfGuestEnabled,
      unitTypes,
      value,
      guestList,
      initialTicketQuantity,
    } = this.props;
    const multiSelectDropdownClasses = classnames(
      'ui',
      'multi-select-dropdown',
      {
        disabled: this.props.disabled,
      }
    );

    return (
      <button
        type="button"
        data-testid="multi-select-dropdown"
        ref="multiSelectDropdown"
        className={multiSelectDropdownClasses}
        onClick={this.expand}
        onKeyDown={(e) => isActionKey(e.key) && this.expand()}
      >
        <div className="text">
          {!for_guest && isGuestOfGuestEnabled
            ? buildEditBookingLabel(unitTypes, guestList)
            : buildCollapsedLabel(unitTypes, value, initialTicketQuantity)}
        </div>
        <i className="af-triangle-down-2" />
      </button>
    );
  }

  _renderMultiSelectOptions() {
    const {
      options,
      slotTicketCapacities,
      tourTicketCapacities,
      for_guest: forGuest,
      renderToolTip,
      inPlaceEdit,
      hasTicketCapacityEnabled,
      isAAB,
    } = this.props;
    return options?.map((option, index) => {
      if (option.internalOnly && forGuest) {
        return null;
      }

      const slotTicketCapacity = slotTicketCapacities?.find(
        (capacity) => capacity.unit_type_id === option.id
      );
      const tourTicketCapacity = tourTicketCapacities?.find(
        (capacity) => capacity.unit_type_id === option.id
      );

      return (
        <MultiSelectOption
          key={option.id}
          id={option.id}
          name={option.noun_plural}
          description={option.description}
          price={option.price}
          quantity={option.count}
          renderToolTip={renderToolTip}
          onDecrease={this.onDecrease}
          onIncrease={this.onIncrease}
          onChange={this.onChange}
          batchUnitsUpdate={this.props.batchUnitsUpdate}
          inPlaceEdit={inPlaceEdit}
          decreasable={this.isDecreasable()}
          increasable={true}
          onExpandIdChange={(id) => this.setState({ expandedId: id })}
          expandedId={this.state.expandedId}
          hasTicketCapacityEnabled={hasTicketCapacityEnabled}
          slotTicketCapacity={slotTicketCapacity}
          tourTicketCapacity={tourTicketCapacity}
          isAAB={isAAB}
        />
      );
    });
  }

  _checkCapacityExceeded(capacities) {
    const { value, options, tourTicketCapacities } = this.props;
    return value.some((selectedValue) => {
      const selectedOption = options.find(
        (option) => option.id === selectedValue.id
      );
      if (!selectedOption || selectedOption.count === 0) return false;

      const matchingCapacity = capacities?.find(
        (capacity) => capacity.unit_type_id === selectedOption.id
      );
      if (!matchingCapacity) return false;

      const limit =
        capacities === tourTicketCapacities
          ? matchingCapacity.capacity
          : matchingCapacity.availability;

      return selectedOption.count > limit;
    });
  }

  _renderCapacityWarning() {
    return (
      <div
        ref="multiSelectOptionsWarning"
        className="multi-select-warning"
        data-testid="multi-select-warning"
      >
        <WarningIcon />
        <div data-testid="ticket-capacity-warning">
          {t(
            'THE_TOTAL_NUMBER_OF_GUESTS_SELECTED_EXCEEDS_THE_MAXIMUM_CAPACITY'
          )}
        </div>
      </div>
    );
  }

  _renderWarning() {
    let warning = null;
    let noun = null;
    let minOrMax = null;
    const {
      min,
      max,
      rootLabels,
      capacityError,
      capacityErrorJobInProgress,
      totalCount,
      slotTicketCapacities,
      options,
      hasTicketCapacityEnabled,
      value,
      timeSelected,
      tourTicketCapacities,
      isAAB,
    } = this.props;
    const totalQuantity = totalCount;

    if (totalQuantity > max) {
      noun = max === 1 ? rootLabels.singular : rootLabels.plural;
      minOrMax = 'maximum';
    } else if (totalQuantity < min) {
      noun = min === 1 ? rootLabels.singular : rootLabels.plural;
      minOrMax = 'minimum';
    }

    const isMaximum = minOrMax === 'maximum';

    if (hasTicketCapacityEnabled && !isAAB) {
      if (!timeSelected && slotTicketCapacities.length === 0) {
        if (this._checkCapacityExceeded(tourTicketCapacities)) {
          warning = this._renderCapacityWarning();
        }
      }

      if (this._checkCapacityExceeded(slotTicketCapacities)) {
        warning = this._renderCapacityWarning();
      }
    }

    if (minOrMax && noun) {
      warning = (
        <div
          ref="multiSelectOptionsWarning"
          className="multi-select-warning"
          data-testid="multi-select-warning"
        >
          <WarningIcon />
          <div>
            {' '}
            {isMaximum
              ? ' ' +
                `${t(`A_MAXIMUM_OF_GUESTS_ALLOWED_PER_BOOKING`, '', {
                  count: max,
                  noun,
                })}`
              : ' ' +
                `${t(`A_MINIMUM_OF_GUESTS_IS_REQUIRED_TO_BOOK`, '', {
                  count: min,
                  noun,
                })}`}
          </div>
        </div>
      );
    }

    if (capacityError?.length > 0) {
      warning = (
        <div
          ref="multiSelectOptionsWarning"
          className="multi-select-warning"
          data-testid="multi-select-warning"
        >
          <WarningIcon />
          <div data-testId="ticket-capacity-warning">
            {t(
              'THE_TOTAL_NUMBER_OF_GUESTS_SELECTED_EXCEEDS_THE_MAXIMUM_CAPACITY'
            )}
          </div>
        </div>
      );
    }

    if (capacityErrorJobInProgress && capacityErrorJobInProgress.length > 0) {
      warning = (
        <div
          ref="multiSelectOptionsWarning"
          className="multi-select-warning"
          data-testid="multi-select-warning"
        >
          <WarningIcon />
          <div data-testId="ticket-capacity-job-in-progress">
            {t(
              'THERE_ARE_UPDATES_IN_PROGRESS_TO_CONTINUE_PLEASE_REFRESH_THE_PAGE'
            )}
          </div>
        </div>
      );
    }

    return warning;
  }

  _renderMultiSelect() {
    const { totalCount, min, max } = this.props;
    const disabled = totalCount < min || totalCount > max;
    return (
      <div ref="multiSelectOptionsContainer" tabIndex={0} role="listbox">
        <div
          data-testid="multi-selector"
          ref="multiSelectOptions"
          className="ui multi-select-options"
        >
          {this._renderMultiSelectOptions()}
        </div>
        {this._renderWarning()}
        <div
          ref="multiSelectDone"
          className="multi-select-done-wrapper"
          data-testid="done-btn"
        >
          <button
            type="button"
            aria-label="Ticket selection complete"
            className={`ui button Button multi-select-done brand-bg ${
              disabled && 'disabled'
            }`}
            onClick={this.multiSelectDone}
            onKeyDown={(e) => isActionKey(e.key) && this.multiSelectDone()}
            data-testid="done-button"
          >
            {t('DONE')}
          </button>
        </div>
      </div>
    );
  }

  _renderMultiSelectGuest() {
    const {
      guestList,
      removeGuest,
      units,
      addGuest,
      updateGuest,
      country,
      onUpdateGuestDetail,
      slotTicketCapacities,
    } = this.props;
    const { isMobile } = Theme.mobile;
    if (!isMobile) {
      return (
        <MultiSelectGuestDesktop
          guestList={guestList}
          isMobile={isMobile}
          multiSelectDone={this.multiSelectDone}
          removeGuest={removeGuest}
          units={units.items}
          addGuest={addGuest}
          updateGuest={updateGuest}
          onUpdateGuestDetail={onUpdateGuestDetail}
          country={country}
          slotTicketCapacities={slotTicketCapacities}
        />
      );
    }
    return '';
  }

  render() {
    const {
      isGuestOfGuestEnabled,
      guestList,
      removeGuest,
      units,
      addGuest,
      updateGuest,
      country,
      slotTicketCapacities,
      isEditingCurrentSlot,
      overbookedTicketCapacities = [],
      hasBookingUpdated = false,
      onUpdateGuestDetail,
    } = this.props;
    const { expanded, showGuestListModal } = this.state;
    const shouldShowGogEditBooking = isGuestOfGuestEnabled;
    const renderMultiSelectComponent = shouldShowGogEditBooking
      ? this._renderMultiSelectGuest()
      : this._renderMultiSelect();
    return (
      <div
        ref="multiSelectContainer"
        className="multi-select-wrapper"
        data-testid="multiselect-dropdown"
      >
        {!expanded ? this._renderDropdown() : renderMultiSelectComponent}
        {showGuestListModal && (
          <GuestListModal
            isOpen={showGuestListModal}
            guestList={guestList}
            isMobile={Theme.mobile.isMobile}
            removeGuest={removeGuest}
            onClose={this.onCloseGuestModal}
            onDone={() => {
              this.setState({ showGuestListModal: false, expanded: false });
            }}
            units={units.items}
            addGuest={addGuest}
            updateGuest={updateGuest}
            onUpdateGuestDetail={onUpdateGuestDetail}
            country={country}
            slotTicketCapacities={slotTicketCapacities}
          />
        )}
        {/* Capacity Warning on adding/removing guests exceeding capacity */}
        {overbookedTicketCapacities?.length > 0 && isEditingCurrentSlot && (
          <CapacityWarning
            marginTop="10px"
            overBookingMessage={formatOverbookingMessage(
              overbookedTicketCapacities,
              hasBookingUpdated
            )}
          />
        )}
      </div>
    );
  }
}

export default MultiSelectDropdown;
